import React, { useMemo } from 'react';

import EntityName from 'components/Globals/EntityName';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import searchQueries from 'containers/Search/queries';
import workQueries from 'containers/Work/queries';

import { useTranslation } from 'src/i18n';
import useAppContext from 'utils/hooks/useAppContext';

import { FILTER_AGGREGATION_SEARCH, PRODUCTIONS } from 'constants/endpointConstants';
import { AGGREGATION_TYPES, BOOLEAN_SEARCH_PARTS, TP, URL_SLUG_TYPES } from 'constants/index';
import { ENTITY_TYPES } from 'operabase-router/constants';

import { useQuery } from 'utils/react-query';

import classes from './CastingMobileSearch.module.scss';

const CastingSearchQuery = ({ type, operator, workId, roleId, filters, isRoleSelection = false }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { data: work } = useQuery(
    searchQueries.getSelectedOption({
      identifier: workId,
      endpoint: FILTER_AGGREGATION_SEARCH(PRODUCTIONS),
      queryParams: {
        work_id: workId,
        aggregation_type: AGGREGATION_TYPES.WORK,
      },
      queryConfig: {
        enabled: !!workId,
      },
    }),
  );

  const { data: castCrewData } = useQuery(
    workQueries.getCastCrewDetails({
      filters: {
        work_id: workId,
        ...filters,
      },
      queryConfig: { enabled: !!workId },
    }),
  );

  const role = useMemo(() => {
    if (!roleId) {
      return null;
    }

    return castCrewData?.reduce((acc, item) => {
      if (!acc) {
        return item?.data?.find(member => member?.id === roleId);
      }
      return acc;
    }, null);
  }, [roleId, castCrewData]);

  if (type === BOOLEAN_SEARCH_PARTS.OPERATOR) {
    return (
      <Typography size="11" color="secondary" italic>
        {operator === ';' ? t(`${TP}.AND`) : t(`${TP}.FN_OR`)}
      </Typography>
    );
  }

  return (
    <span className={classes.castingSearchQuery}>
      {isRoleSelection ? (
        <Typography size="12" weight="bold" color="link" underline>
          {t(`${TP}.SELECT_ROLE`)}
        </Typography>
      ) : (
        <Typography size="12" weight="bold">
          {role?.name}
        </Typography>
      )}
      <EntityName
        entity={work}
        entityType={ENTITY_TYPES.WORK}
        withComposers
        typographyProps={{
          work: { size: 10, weight: 'medium' },
          composer: { size: 10, color: 'secondary', italic: true },
        }}
        withTranslation={false}
      />
    </span>
  );
};

export default function CastingMobileSearch({ booleanQuery = [], onClick }) {
  const { obRouteContext } = useAppContext();
  const { filters } = obRouteContext?.linkProps || {};

  return (
    <div className={classes.castingSearch} onClick={onClick}>
      <span className={classes.castingSearch__searchSection}>
        <SpriteIcon icon="search" size="14" />
      </span>
      <Typography className={classes.castingSearch__query} truncate>
        {booleanQuery?.map(query => (
          <CastingSearchQuery
            type={query?.type}
            operator={query?.value}
            workId={query?.value?.[URL_SLUG_TYPES.WORK]}
            roleId={query?.value?.[URL_SLUG_TYPES.WORK_ROLE]?.id}
            filters={filters}
            key={`${query?.value?.[URL_SLUG_TYPES.WORK]}-${query?.value?.[URL_SLUG_TYPES.WORK_ROLE]?.id}`}
          />
        ))}
      </Typography>
    </div>
  );
}
